import { ColumnDef } from '@tanstack/react-table';
import * as _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import {
  GetUserTablePreferenceDocumentGQLType,
  useGetUserTablePreferenceQuery,
  useUpsertUserTablePreferenceMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

export type ColumnConfiguration<ColumnKey extends string> = {
  columnKey: ColumnKey;
  isVisible: boolean;
  isFixed: boolean;
};

export const getColumnNames = (
  columns: ColumnDef<any, any>[],
): Record<string, string> => {
  return columns.reduce<Record<string, string>>((acc, column) => {
    const { id, header } = column;
    finleyAssert(id, 'Column must have an id');

    return { ...acc, [id]: typeof header === 'string' ? header : id };
  }, {});
};

/**
 * This is a list of keys that uniquely identify a table on the dashboard to be able to store user preference for column configuration
 *
 * Please add a new key to this list if you are adding a new table to the dashboard
 *
 * The naming convention follows the following pattern:
 *
 * <DOMAIN>.<TABLE_NAME>
 *
 * #### DOMAINS ####
 * - valleyBankSbl - valley bank's SBL dashboard
 */
const TABLE_CONFIGURATION_UNIQUE_KEYS = [
  // valley bank assignment review page that shows holdings table (/assignment)
  'valleyBankSbl.assignmentHoldings',
  // valley bank sbl dashboard arrangement group loan accounts table (/arrangement-groups)
  'valleyBankSbl.arrangementGroupLoanAccounts',
  // valley bank sbl dashboard arrangement group collateral accounts table (/arrangement-groups)
  'valleyBankSbl.arrangementGroupCollateralAccounts',
  // valley bank sbl dashboard customers table (/customers)
  'valleyBankSbl.customers',
  // valley bank sbl dashboard customer summary loans table (/customers/summary)
  'valleyBankSbl.customerLoans',
  // valleyBank sbl dashboard customer holdings page holdings table (/customers/holdings)
  'valleyBankSbl.customerHoldings',
  // valley bank customer summary page collateral accounts table (/customers/summary)
  'valleyBankSbl.customerCollateralAccounts',
  // valley bank sbl dashboard loan account loan summary table (/loan-accounts/summary)
  'valleyBankSbl.loanAccountLoanSummary',
  // valley bank sbl dashboard loan account holdings tab holdings table (/loan-accounts/holdings)
  'valleyBankSbl.loanAccountHoldings',
  // valley bank sbl home page (/loan-accounts)
  'valleyBankSbl.loanAccounts',
  // valley bank sbl home page (/loan-accounts/under-margin-report) on current or historical
  'valleyBankSbl.loanAccountsUnderMarginNonPreview',
  // valley bank sbl home page (/loan-accounts/under-margin-report) on preview
  'valleyBankSbl.loanAccountsUnderMarginPreview',
  // valley bank sbl loanAccount detail summary page assigned collateral accounts table (/loan-accounts/summary)
  'valleyBankSbl.loanAccountAssignedCollateralAccounts',
  // facilities table found on home page of borrower portal
  'core.creditFacilities.facilitiesTable',
  // Invoices table (/invoices)
  'invoices.invoicesTable',
  // Funding requests table (/funding-requests)
  'fundingRequests.historyTable',
] as const;

export type TableColumnConfigurationKey = typeof TABLE_CONFIGURATION_UNIQUE_KEYS[number];

type TableColumnPreferencesOperations<ColumnKey extends string> = {
  columnConfiguration: ColumnConfiguration<ColumnKey>[];
  columnOrder: ColumnConfiguration<ColumnKey>['columnKey'][];
  columnVisibility: Record<ColumnKey, boolean>;
  updateUserColumnConfiguration: (
    columnConfiguration: ColumnConfiguration<ColumnKey>[],
  ) => void;
};

export type DefaultTableColumnConfiguration<ColumnKey extends string> = {
  tableKey: TableColumnConfigurationKey;
  columns: ColumnConfiguration<ColumnKey>[];
};

const isValidColumnConfiguration = <ColumnKey extends string>(
  column: { columnKey: string; isVisible: boolean; isFixed: boolean },
  validKeys: ColumnKey[],
): column is ColumnConfiguration<ColumnKey> => {
  return validKeys.includes(column.columnKey as ColumnKey);
};

export const createDefaultTableColumnConfiguration = <
  ColumnKey extends string
>({
  tableKey,
  columns,
}: DefaultTableColumnConfiguration<ColumnKey>): DefaultTableColumnConfiguration<ColumnKey> => {
  // validate column keys are unique

  const columnKeys = columns.map((column) => column.columnKey);
  const uniqueColumnKeys = new Set(columnKeys);

  finleyAssert(
    columnKeys.length === uniqueColumnKeys.size,
    `Column keys must be unique. Duplicate keys [${columnKeys.filter((val, i) =>
      columnKeys.includes(val, i + 1),
    )}]`,
  );

  return {
    tableKey,
    columns,
  };
};

export const useTableColumnPreferences = <ColumnKey extends string = string>({
  tableKey,
  defaultTableColumnConfiguration,
  skipSettingConfig = false,
}: {
  tableKey: TableColumnConfigurationKey;
  defaultTableColumnConfiguration: DefaultTableColumnConfiguration<ColumnKey>;
  skipSettingConfig?: boolean;
}): TableColumnPreferencesOperations<ColumnKey> => {
  finleyAssert(
    tableKey === defaultTableColumnConfiguration.tableKey,
    `Invalid default column configuration passed in. Expected table [${tableKey}], received configuration for [${defaultTableColumnConfiguration.tableKey}]`,
  );

  // @jendevelops if no table configuration returned for user in db, will use default configuration

  const [columnConfiguration, setColumnConfiguration] = useState<
    ColumnConfiguration<ColumnKey>[]
  >(defaultTableColumnConfiguration.columns);

  const columnKeys = useMemo(() => {
    return defaultTableColumnConfiguration.columns.map(
      (column) => column.columnKey,
    );
  }, [defaultTableColumnConfiguration]);

  const columnOrder = useMemo(() => {
    return columnConfiguration.map((column) => column.columnKey);
  }, [columnConfiguration]);

  const columnVisibility = useMemo(() => {
    return columnConfiguration.reduce((acc, column) => {
      return { ...acc, [column.columnKey]: column.isVisible };
    }, {} as Record<ColumnKey, boolean>);
  }, [columnConfiguration]);

  const { data } = useGetUserTablePreferenceQuery({
    variables: {
      tableKey,
      defaultTableColumnConfiguration,
    },
  });

  const [upsertUserTablePreference] = useUpsertUserTablePreferenceMutation();

  useEffect(() => {
    const userTablePreference = data?.getUserTablePreference;
    if (
      !skipSettingConfig &&
      userTablePreference &&
      !_.isEqual(userTablePreference.config.columns, columnConfiguration)
    ) {
      const {
        config: { columns },
      } = userTablePreference;

      // Filter columns to only include those that exist in the current columnKeys
      // This handles cases where the server has cached configurations with outdated column keys
      const validColumns = columns.filter((column) =>
        isValidColumnConfiguration(column, columnKeys),
      );

      // If we have valid columns and they differ from current configuration, update
      if (validColumns.length > 0) {
        setColumnConfiguration(validColumns);
      } else {
        // If no valid columns, fall back to default configuration
        setColumnConfiguration(defaultTableColumnConfiguration.columns);
      }
    }
  }, [
    columnKeys,
    columnConfiguration,
    data,
    skipSettingConfig,
    defaultTableColumnConfiguration,
  ]);

  const updateUserColumnConfiguration = useCallback(
    (newColumnConfiguration: ColumnConfiguration<ColumnKey>[]) => {
      if (!_.isEqual(columnConfiguration, newColumnConfiguration)) {
        const sortedPreviousColumnConfiguration = [...columnConfiguration].sort(
          (a, b) => {
            return (
              newColumnConfiguration.findIndex(
                (column) => column.columnKey === a.columnKey,
              ) -
              newColumnConfiguration.findIndex(
                (column) => column.columnKey === b.columnKey,
              )
            );
          },
        );

        const newConfiguration = sortedPreviousColumnConfiguration.reduce(
          (acc, column) => {
            const newColConfiguration = newColumnConfiguration.find(
              (currCol) => currCol.columnKey === column.columnKey,
            );

            if (!newColConfiguration) {
              return acc;
            }

            if (newColConfiguration.isVisible !== column.isVisible) {
              return [
                ...acc,
                {
                  ...column,
                  isVisible: newColConfiguration.isVisible,
                },
              ];
            }

            return [...acc, column];
          },
          [] as ColumnConfiguration<ColumnKey>[],
        );

        upsertUserTablePreference({
          variables: {
            tablePreference: {
              tableKey,
              config: {
                columns: newColumnConfiguration,
              },
            },
            defaultTableColumnConfiguration,
          },
          refetchQueries: [
            {
              query: GetUserTablePreferenceDocumentGQLType,
              variables: {
                tableKey,
                defaultTableColumnConfiguration,
              },
            },
          ],
        }).then(() => {
          setColumnConfiguration(newConfiguration);
        });
      }
    },
    [
      columnConfiguration,
      defaultTableColumnConfiguration,
      tableKey,
      upsertUserTablePreference,
      setColumnConfiguration,
    ],
  );

  return {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  };
};
